import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@design-system';
import { ChevronRight, Mail, UserPen, ArrowLeft, Loader2 } from 'lucide-react';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigateToIntakeForm } from '@client/hooks/useNavigateToIntakeForm';

import { z } from 'zod';
import { toast } from 'sonner';
import { zodResolver } from '@hookform/resolvers/zod';
import { getIntroIntakeFormApiEndPoint } from '@shared';
import { selectMaybeClient, selectSessionAdvisor } from '@/model';
import { WaterlilyClient } from '@/api/waterlily';
import { inviteApi } from '@/features/invite-client';

type QuestionnaireMode = 'send-to-client' | 'complete-for-client';

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type EmailFormValues = z.infer<typeof emailSchema>;

const MODE_OPTIONS = [
  {
    mode: 'send-to-client' as const,
    icon: <Mail className="size-5" />,
    title: 'Send to client',
    description: 'Email the medical questionnaire to the client to complete on their own.',
  },
  {
    mode: 'complete-for-client' as const,
    icon: <UserPen className="size-5" />,
    title: 'Complete on behalf of client',
    description: "Open and fill out the questionnaire yourself using the client's information.",
  },
];

type QuestionnaireDialogVariant = 'default' | 'underwriting';

interface QuestionnaireDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: QuestionnaireDialogVariant;
  client?: Client;
  onBack?: () => void;
}

interface QuestionnaireContentProps {
  client: Client;
  variant?: QuestionnaireDialogVariant;
  onBack?: () => void;
  onClose: () => void;
}

export function QuestionnaireContent({ client, variant = 'default', onBack, onClose }: QuestionnaireContentProps) {
  const advisor = useSelector(selectSessionAdvisor);
  const { navigateToIntakeForm, isLoading: isNavigating } = useNavigateToIntakeForm({ clientId: client.clientId });
  const [mode, setMode] = useState<QuestionnaireMode | null>(null);
  const [isSending, setIsSending] = useState(false);

  const isLoading = isNavigating || isSending;

  const form = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });

  function handleSelectMode(selected: QuestionnaireMode) {
    if (selected === 'send-to-client') {
      if (client.clientEmail) {
        sendFormToClient(client.clientEmail);
      } else {
        setMode('send-to-client');
      }
    } else {
      completeForClient();
    }
  }

  async function saveClientEmail(email: string) {
    await WaterlilyClient.put(`/client/${client.clientId}`, {
      ...client,
      clientEmail: email,
    });
  }

  async function sendFormToClient(email: string) {
    setIsSending(true);
    try {
      if (email !== client.clientEmail) {
        await saveClientEmail(email);
      }

      const intakeFormLink = getIntroIntakeFormApiEndPoint(advisor.advisorId, false);

      await inviteApi.sendInvite({
        clientId: client.clientId,
        advisorId: advisor.advisorId,
        email,
        clientFirstName: client.clientFirstName,
        clientLastName: client.clientLastName,
        intakeFormLink,
      });

      onClose();
      toast.success('Questionnaire sent successfully');
    } finally {
      setIsSending(false);
    }
  }

  async function completeForClient() {
    const placeholderEmail = `${client.clientId}@noemail.waterlily.internal`;
    if (!client.clientEmail) {
      await saveClientEmail(placeholderEmail);
    }
    await navigateToIntakeForm();
    onClose();
  }

  function handleEmailSubmit(values: EmailFormValues) {
    sendFormToClient(values.email);
  }

  const { title, description } = getDialogCopy(mode, variant);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>

      {mode === null && onBack && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={onBack}
        >
          <ArrowLeft />
          Back
        </Button>
      )}

      {mode !== null && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={() => {
            setMode(null);
            form.reset();
          }}
        >
          <ArrowLeft />
          Back
        </Button>
      )}

      {mode === null && (
        <div className="flex flex-col gap-3">
          {MODE_OPTIONS.map(
            ({ mode: optionMode, icon, title: optionTitle, description: optionDesc }) => (
              <button
                key={optionMode}
                type="button"
                disabled={isLoading}
                onClick={() => handleSelectMode(optionMode)}
                className="hover:border-primary hover:bg-primary/5 border-border bg-background group flex w-full cursor-pointer items-center gap-4 rounded-lg border p-4 text-left transition-all disabled:pointer-events-none disabled:opacity-50"
              >
                <div className="group-hover:bg-primary text-foreground bg-muted group-hover:text-background flex size-12 shrink-0 items-center justify-center rounded-full transition-all">
                  {icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-950">{optionTitle}</h3>
                  <p className="text-foreground text-sm">{optionDesc}</p>
                </div>
                {isLoading ? (
                  <Loader2 className="text-muted-foreground size-5 animate-spin" />
                ) : (
                  <ChevronRight className="group-hover:text-primary text-muted-foreground size-5 transition-all" />
                )}
              </button>
            ),
          )}
        </div>
      )}

      {mode === 'send-to-client' && (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleEmailSubmit)}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Client email <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="jane@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="mt-4">
              <Button
                type="submit"
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="animate-spin" />}
                Send Questionnaire
              </Button>
            </DialogFooter>
          </form>
        </Form>
      )}
    </>
  );
}

export function QuestionnaireDialog({ open, onOpenChange, variant = 'default', client: clientProp, onBack }: QuestionnaireDialogProps) {
  const reduxClient = useSelector(selectMaybeClient);
  const client = clientProp ?? reduxClient!;

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <QuestionnaireContent
          client={client}
          variant={variant}
          onBack={onBack ? () => { handleOpenChange(false); onBack(); } : undefined}
          onClose={() => handleOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function getDialogCopy(mode: QuestionnaireMode | null, variant: QuestionnaireDialogVariant) {
  if (mode === 'send-to-client') {
    return {
      title: 'Send to Client',
      description: "Enter the client's email address to send the medical questionnaire.",
    };
  }

  if (variant === 'underwriting') {
    return {
      title: 'Complete Medical Questionnaire',
      description:
        'Complete the medical questionnaire to enable AI-powered underwriting analysis. Once submitted, eligibility insights will automatically appear on each product.',
    };
  }

  return {
    title: 'Complete Medical Questionnaire',
    description: "Choose how you'd like to complete the questionnaire.",
  };
}
